<template>
  <div v-if="canLoad" class="form-group position-relative">
    <label v-if="label" class="font-size-12">{{ label }}</label>
    <div class="input-container d-flex">
      <input v-model="iValue" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" type="text" class="form-control" placeholder="https://example.com" :style="{ flex: '1 1 auto' }" />
      <button :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="Upload" @click="openUploadModal">
        <b-spinner v-if="loadingUpload" small />
        <i v-else :class="uploadIcon" />
      </button>
      <button v-if="splashImage" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="Images" @click="showImagesModal">
        <i :class="imagesIcon" />
      </button>
      <button v-if="user.agency.openai_api_key" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="AI Image" @click="openAIModal">
        <i :class="magicIcon" />
      </button>
      <button :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload btn-remove" title="Delete" @click="removeImage">
        <i :class="deleteIcon" />
      </button>
      <input ref="inputFile" type="file" name="upload" :style="{ display: 'none' }" accept="image/*" @change="uploadFile($event)" />
    </div>
    <b-modal v-model="showSplashModal" title="SplashImages" hide-footer>
      <div ref="splashImages" class="splash-images" :data-id="dataId">
        <div class="input-container d-flex" :data-id="dataId">
          <input v-model="query" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" type="text" class="form-control" placeholder="Search..." :style="{ flex: '1 1 auto' }" :data-id="dataId" @keyup.enter="searchImages" />
          <button class="btn-upload" title="Images" :disabled="!!(!query || loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" :data-id="dataId" @click="searchImages">
            <b-spinner v-show="loadingImages" small />
            <i v-show="!loadingImages" :class="`${magnifyingIcon} disabled`" />
          </button>
        </div>
        <div class="list-images" :data-id="dataId">
          <img v-for="(img, index) in splashImages" :key="index" :src="img.urls.regular" :data-id="dataId" @click="selectImage(img)" />
        </div>
        <div v-if="splashImages.length" class="text-center pt-3" :data-id="dataId">
          <b-button variant="outline-primary" size="sm" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" :data-id="dataId" @click="loadMoreImages">
            <b-spinner v-show="loadingMoreImages" small />
            <span v-show="!loadingMoreImages" class="disabled">More images</span>
          </b-button>
        </div>
      </div>
    </b-modal>
    <b-modal v-model="showAIModal" title="Generate AI Image" hide-footer>
      <div class="mb-3">
        <label>Describe the image you want:</label>
        <textarea v-model="aiPrompt" class="form-control" rows="3" placeholder="Generate a stunning, high-quality image..." />
      </div>
      <div v-if="aiGeneratedImage" class="text-center mb-3">
        <img :src="aiGeneratedImage" alt="AI Preview" style="max-width: 100%; border-radius: 8px;" />
      </div>
      <div class="mb-3">
        <label>Suggested Image Name (AI):</label>
        <div style="position: relative;">
          <input
            type="text"
            class="form-control"
            v-model="uploadAiName"
            :disabled="generatingAiName"
          />
          <div
            v-if="generatingAiName"
            style="position: absolute; top: 10px; left: 10px; 
                  align-items: center; justify-content: center;">
            <b-spinner small />
          </div>
        </div>
      </div>  
      <div class="d-flex justify-content-between">
        <b-button :disabled="loadingGenerate || !aiPrompt" variant="primary" @click="generateAIImage">
          <b-spinner small v-if="loadingGenerate" />
          <span v-if="!loadingGenerate">{{ aiGeneratedImage ? 'Regenerate' : 'Generate' }}</span>
        </b-button>
        <b-button :disabled="loadingGenerate || !aiPrompt" v-if="aiGeneratedImage && !loadingGenerate" variant="success" @click="editAIImage">
          Edit Image
        </b-button>
        <b-button v-if="aiGeneratedImage && !loadingGenerate" variant="success" @click="keepAIImage">
          Use This Image
        </b-button>
        <b-button variant="secondary" @click="closeAIModal">Close</b-button>
      </div>
    </b-modal>
    <b-modal v-model="showUploadModal" title="Upload Image" hide-footer>
      <div class="mb-2">
        <label>Select an image:</label>
        <input ref="uploadFileInput" type="file" class="form-control" accept="image/*" @change="handleFileSelect" />
      </div>

      <div v-if="uploadPreview" class="mb-3 text-center">
        <img :src="uploadPreview" style="max-width: 100%; border-radius: 6px;" />
      </div>

      <div class="mb-3">
        <label>Suggested Image Name (AI):</label>
        <div style="position: relative;">
          <input
            type="text"
            class="form-control"
            v-model="uploadAiName"
            :disabled="generatingAiName"
          />
          <div
            v-if="generatingAiName"
            style="position: absolute; top: 10px; left: 10px; 
                  align-items: center; justify-content: center;">
            <b-spinner small />
          </div>
        </div>
      </div>  

      <div class="d-flex justify-content-between">
        <b-button :disabled="loadingUpload || !selectedFile || generatingAiName" variant="primary" @click="submitUploadImage">
          <b-spinner small v-if="loadingUpload" />
          <span v-else>Upload</span>
        </b-button>
        <b-button variant="secondary" @click="closeUploadModal">Cancel</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import './Styles/ImageInput2.scss'
import { DELETE_ICON, IMAGES_ICON, UPLOAD_ICON, MAGNIFYING_ICON, MAGIC_ICON } from '@src/icons'
export default {
  props: {
    label: { type: [String], default: '' },
    splashImage: { type: [Boolean], default: true },
    options2: { type: [Object], default: null, required: true },
    elementId: { type: String, default: null, required: true },
    valueKey: { type: String, default: null, required: true }
  },
  data() {
    return {
      deleteIcon: DELETE_ICON,
      imagesIcon: IMAGES_ICON,
      uploadIcon: UPLOAD_ICON,
      magnifyingIcon: MAGNIFYING_ICON,
      magicIcon: MAGIC_ICON,
      loadingImages: false,
      loadingMoreImages: false,
      splashImages: [],
      query: '',
      page: 1,
      timer: null,
      loadingGenerate: false,
      showAIModal: false,
      aiPrompt: '',
      aiGeneratedImage: '',
      showUploadModal: false,
      selectedFile: null,
      uploadPreview: '',
      uploadAiName: '',
      generatingAiName: false,
      aiGeneratedBase64: '',
      showSplashModal: false
    }
  },
  computed: {
    user() { return this.$store.getters['auth/user'] },
    canLoad() {
      if (this.options2[this.elementId] && this.options2[this.elementId].hasOwnProperty(this.valueKey)) {
        return true
      }
      return false
    },
    dataId() {
      return `${this.elementId}:${this.valueKey}`
    },
    imageUploadStatus2: {
      get() { return this.$store.getters['utility/imageUploadStatus2'] },
      set(value) { this.$store.dispatch('utility/setImageUploadStatus2', value) }
    },
    loadingUpload() {
      return this.imageUploadStatus2[`${this.elementId}:${this.valueKey}`] || false
    },
    iValue: {
      get() {
        return this.options2[this.elementId][this.valueKey]
      },
      set(newVal) {
        this.options2[this.elementId][this.valueKey] = newVal
        this.options2[this.elementId].Ratio = 0
        if (this.timer) {
          clearTimeout(this.timer)
        }
        this.timer = setTimeout(() => {
          let that = this
          let img = new Image()
          img.onload = function() {
            let ratio = 0
            if (this.width && this.height && !isNaN(this.width) && !isNaN(this.height) && parseFloat(this.width) > 0) {
              const width = parseFloat(this.width)
              const height = parseFloat(this.height)
              ratio = parseFloat(parseFloat(height / width).toFixed(2))
            }
            that.options2[that.elementId].Ratio = ratio
          }
          img.src = newVal
        }, 500)
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.toggleSplashImages)
  },
  methods: {
    setImageUploadStatus2(value) {
      this.imageUploadStatus2 = value
    },
    selectFile() {
      this.$refs.inputFile.click()
    },
    uploadFile(event) {
      if (!event.target.files.length) return

      let imageParams = new FormData()
      imageParams.append('file', event.target.files[0])
      imageParams.append('resize-image', false)
      imageParams.append('elementId', this.elementId)
      imageParams.append('valueKey', this.valueKey)

      this.setImageUploadStatus2({
        ...this.imageUploadStatus2,
        [`${this.elementId}:${this.valueKey}`]: true
      })
      this.$store
        .dispatch('image/uploadImage', imageParams)
        .then((response) => {
          if (response.url && this.options2 && this.options2[response.elementId]) {
            this.options2[response.elementId][response.valueKey] = response.url
            this.options2[response.elementId].Ratio = response.Ratio || 0
          }
          this.setImageUploadStatus2({
            ...this.imageUploadStatus2,
            [`${response.elementId}:${response.valueKey}`]: false
          })
        })
        .catch((callParams) => {
          const elementId = callParams.get('elementId')
          const valueKey = callParams.get('valueKey')
          this.setImageUploadStatus2({
            ...this.imageUploadStatus2,
            [`${elementId}:${valueKey}`]: false
          })
        })
    },
    loadMoreImages() {
      this.page += 1
      this.loadingMoreImages = true
      this.$store
        .dispatch('image/getUnsplashImages', { query: this.query, page: this.page })
        .then((images) => {
          this.splashImages = [ ...this.splashImages, ...images ]
          this.loadingMoreImages = false
        })
        .catch(() => this.loadingMoreImages = false)
    },
    searchImages() {
      this.page = 1
      this.getImages()
    },
    getImages() {
      this.splashImages = []
      this.loadingImages = true
      this.$store
        .dispatch('image/getUnsplashImages', { query: this.query, page: this.page })
        .then((images) => {
          this.splashImages = images
          this.loadingImages = false
          if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'block'
        })
        .catch(() => this.loadingImages = false)
    },
    async selectImage(img) {
      let Ratio = 0;
      if (img.width && img.height) {
        Ratio = parseFloat(img.height / img.width);
        Ratio = parseFloat(Ratio.toFixed(2));
      }

      const filename = this.query;
      const payload = {
        image_url: img.urls.regular,
        filename,
        landing_id: this.$route.params.id,
        elementId: this.elementId,
        valueKey: this.valueKey,
      };

      this.setImageUploadStatus2({
        ...this.imageUploadStatus2,
        [`${this.elementId}:${this.valueKey}`]: true
      });

      try {
        const response = await this.$store.dispatch('image/saveUnsplashImage', payload);
        if (response.url && this.options2 && this.options2[response.elementId]) {
          this.options2[response.elementId][response.valueKey] = response.url;
          this.options2[response.elementId].Ratio = response.Ratio || Ratio;
        }
      } catch (e) {
        this.showErrorMessage('Failed to save Unsplash image.');
      } finally {
        this.setImageUploadStatus2({
          ...this.imageUploadStatus2,
          [`${this.elementId}:${this.valueKey}`]: false
        });
      }

    },
    toggleSplashImages(event) {
      const dataId = event.target.getAttribute('data-id')
      if (!dataId) {
        if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'none'
        document.removeEventListener('click', this.toggleSplashImages)
      }
    },
    removeImage() {
      this.options2[this.elementId][this.valueKey] = null
      this.options2[this.elementId].Ratio = 0
    },
    showImagesModal(event) {
      this.showSplashModal = true
      event.stopPropagation()
      document.addEventListener('click', this.toggleSplashImages)
      if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'block'
    },
    openAIModal() {
      this.showAIModal = true
      this.uploadAiName = '';
      this.aiGeneratedImage = this.options2[this.elementId][this.valueKey]
    },
    closeAIModal() {
      this.showAIModal = false
      this.aiGeneratedImage = ''
    },
    openUploadModal() {
      this.showUploadModal = true;
      this.selectedFile = null;
      this.uploadAiName = '';
      this.uploadPreview = '';

      this.$nextTick(() => {
      if (this.$refs.uploadFileInput) {
        this.$refs.uploadFileInput.value = '';
        }
      });
    },

    closeUploadModal() {
      this.showUploadModal = false;
    },

    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.uploadPreview = URL.createObjectURL(file);
      this.generatingAiName = true;

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('generate_name_only', true);
        formData.append('agency_id', this.user.agency.id);

        const response = await this.$store.dispatch('image/generateImageNameFromFile', formData);
        if (response.seoFilename) {
          this.uploadAiName = response.seoFilename;
        }
      } catch (e) {
        this.uploadAiName = 'image-' + Date.now();
      } finally {
        this.generatingAiName = false;
      }
    },

    async submitUploadImage() {
      if (!this.selectedFile || !this.uploadAiName) return;

      this.setImageUploadStatus2({
        ...this.imageUploadStatus2,
        [`${this.elementId}:${this.valueKey}`]: true
      });

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('filename', this.uploadAiName);
      formData.append('landing_id', this.$route.params.id);
      formData.append('elementId', this.elementId);
      formData.append('valueKey', this.valueKey);

      try {
        const response = await this.$store.dispatch('image/uploadImageWithName', formData);
        if (response.url && this.options2 && this.options2[response.elementId]) {
          this.options2[response.elementId][response.valueKey] = response.url;
          this.options2[response.elementId].Ratio = response.Ratio || 0;
          }
      } finally {
        this.setImageUploadStatus2({
          ...this.imageUploadStatus2,
          [`${this.elementId}:${this.valueKey}`]: false
        });
        this.closeUploadModal();
      }
    },

    async generateAIImage() {
      if (!this.aiPrompt) return

      this.loadingGenerate = true
      this.generatingAiName = true

      try {
        const response = await this.$store.dispatch('image/generateAIImageNew', {
          image_prompt: this.aiPrompt,
          agency_id: this.user.agency.id,
        });

        const slug = response?.slug;
        const base64 = response?.base64;

        if (slug) this.uploadAiName = slug;
        if (base64) {
          this.aiGeneratedBase64 = base64;
          this.aiGeneratedImage = `data:image/webp;base64,${base64}`;
        } else {
          this.showErrorMessage('Image could not be generated. Please try again.');
        }
      } catch (error) {
        this.showErrorMessage('Image generation failed. Please try again later.');
      } finally {
        this.loadingGenerate = false;
        this.generatingAiName = false;
      }
    },

    async editAIImage() {
      if (!this.aiPrompt) return

      this.loadingGenerate = true;
      this.generatingAiName = true;

      try {
        const response = await this.$store.dispatch('image/editAIImage', {
          image_prompt: this.aiPrompt,
          agency_id: this.user.agency.id,
          origin_img: this.aiGeneratedImage,
        })

        const slug = response?.slug;
        const base64 = response?.base64;
  
        if (slug) this.uploadAiName = slug;
        if (base64) {
          this.aiGeneratedBase64 = base64;
          this.aiGeneratedImage = `data:image/webp;base64,${base64}`;
          } else {
            this.showErrorMessage('Image could not be generated. Please try again.');
          }
        } catch (error) {
          this.showErrorMessage('Image generation failed. Please try again later.');
        } finally {
          this.loadingGenerate = false;
          this.generatingAiName = false;
        }
    },
    async keepAIImage() {
      if (!this.uploadAiName || !this.aiGeneratedBase64) {
        this.showErrorMessage('Missing image or filename.');
        return;
      }

      this.loadingGenerate = true;

      try {
        const response = await this.$store.dispatch('image/uploadBaseNamed', {
          base64: this.aiGeneratedBase64,
          filename: this.uploadAiName,
          landing_id: this.$route.params.id,
          elementId: this.elementId,
          valueKey : this.valueKey,
        });

        const url = response?.url;
        if (url) {
          this.options2[this.elementId][this.valueKey] = url;
          this.options2[this.elementId].Ratio = 0;
          this.closeAIModal();
        } else {
          this.showErrorMessage('Upload failed.');
        }
      } catch (err) {
        this.showErrorMessage('Upload to S3 failed.');
      } finally {
        this.loadingGenerate = false;
      }
    },
    showErrorMessage(message) {
      alert(message)
    },
  }
}
</script>